import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { useDebounce } from '@uidotdev/usehooks'
import { Breadcrumb } from 'antd'
import {
  ChangeEvent,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { DELAY } from '@/constants'
import useSearch from '@/hooks/search/useSearch'
import { useCategoriesByBrandsCatalogStore } from '@/stores/feed/categoriesByBrandsCatalogStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  CategoryInterface,
  CharacteristicFilterWithRange,
  NomenclatureGroupInterface,
} from '@/types'
import { isNomenclatureEditor } from '@/utils/auth/userRoles'
import { pickCarriedCharacteristics } from '@/utils/search/characteristics/carriedCharacteristics'
import getPathForSelectedTreeNode from '@/utils/tree/nodes/getPathForSelectedTreeNode'
import { isLastParentNode } from '@/utils/tree/nodes/isLastParentNode'
import searchTree from '@/utils/tree/nodes/searchTree'
import {
  getTreeNodeChildren,
  getTreeNodeDescription,
  getTreeNodeId,
  isCategoryNode,
  type TreeNode,
} from '@/utils/tree/nodes/treeNode'

import BreadcrumbItem from './BreadcrumbItem/BreadcrumbItem'
import cn from './TreeTiles.module.scss'

const Tile = safeLazy(() => import('./Tile/Tile'))
import { filtersService } from '@/services/filters/FiltersService'
import { safeLazy } from '@/utils/app/safeLazy'

type TreeTilesPropsType = {
  tree: CategoryInterface[] | NomenclatureGroupInterface[]
  treeLoading: boolean
  loadingError: string
  hideSearch?: boolean
  actionButton?: React.ReactNode
  setTree?:
    | ((tree: CategoryInterface[]) => void)
    | ((tree: NomenclatureGroupInterface[]) => void)
}

function TreeTiles(props: TreeTilesPropsType) {
  const { tree, treeLoading, loadingError, setTree, hideSearch, actionButton } =
    props

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const currentActiveId =
    searchParams.get('categoryId') || searchParams.get('groupId') || ''

  const [searchValue, setSearchValue] = useState<string>('')

  const debouncedSearchValue = useDebounce(searchValue, DELAY / 2)

  const [rawSearchResults, setRawSearchResults] = useState<TreeNode[][]>([])
  const [searchInputFocused, setSearchInputFocused] = useState<boolean>(false)

  const [modalOpen] = useState(false)

  const searchInputRef = useRef<HTMLInputElement>(null)

  const { selectedOrganisation, currentUser } = useUserInfoStore(
    (state) => state
  )
  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )

  const {
    withLeftovers,
    withDiscontinued,
    currentlySearchableStores,
    currentlySearchableCharacteristics,
  } = useSearchStore((state) => state)

  const { currentBrand } = useCategoriesByBrandsCatalogStore((state) => state)

  const { parentId, setParentId } = useTreeTilesStore((state) => state)

  const search = useSearch()

  const parentRef = useRef<HTMLDivElement>(null)

  const GAP = 32
  const TILE_VERTICAL_PADDING = 16 * 2

  const isSameLocation = (newParams: URLSearchParams) => {
    return newParams.toString() === searchParams.toString()
  }

  useEffect(() => {
    if (!tree || tree.length === 0) return

    if (!currentActiveId) {
      filtersService.resetAllSearchFilters({ keepCarriedCharacteristics: true })
    }
  }, [currentActiveId, tree])

  const handleBreadcrumbNavigate = useCallback(
    (item?: TreeNode, isFirst?: boolean) => {
      const newParams = new URLSearchParams(searchParams)

      if (isFirst) {
        newParams.delete('categoryId')
        newParams.delete('groupId')
        navigate({ search: newParams.toString() }, { replace: true })
        return
      }

      if (item) {
        const id = getTreeNodeId(item)
        if (isCategoryNode(item)) {
          newParams.set('categoryId', id)
          newParams.delete('groupId')
        } else {
          newParams.set('groupId', id)
          newParams.delete('categoryId')
        }
      }

      if (isSameLocation(newParams)) return

      const isJump = location.state?.isJump === true

      if (!isJump && tree.length > 0) {
        const currentCategoryId = searchParams.get('categoryId') || ''
        const currentGroupId = searchParams.get('groupId') || ''
        const activeId = currentCategoryId || currentGroupId
        const targetId = item ? getTreeNodeId(item) : undefined

        if (activeId) {
          const path = getPathForSelectedTreeNode(tree, activeId)
          if (path) {
            const pathIds = path.map(getTreeNodeId)
            const targetIndex = targetId ? pathIds.indexOf(targetId) : -1
            const currentIndex = pathIds.length - 1

            if (targetIndex !== -1 && targetIndex < currentIndex) {
              const delta = currentIndex - targetIndex
              const historyIdx = window.history.state?.idx

              if (historyIdx !== undefined && historyIdx >= delta) {
                navigate(-delta)
                return
              }
            }
          }
        }
      }

      navigate({ search: newParams.toString() })
    },
    [searchParams, navigate, tree, location.state]
  )

  const searchPathsCache = useRef<Record<string, TreeNode[][]>>({})

  useEffect(() => {
    searchPathsCache.current = {}
  }, [tree])

  const breadcrumbItemsForAntd = useMemo(() => {
    const categoryId = searchParams.get('categoryId') || ''
    const groupId = searchParams.get('groupId') || ''
    const activeId = categoryId || groupId

    const items = [
      {
        title: (
          <BreadcrumbItem
            isFirst={true}
            onClick={() => handleBreadcrumbNavigate(undefined, true)}
            showAllLabel={!!activeId}
          />
        ),
        id: '',
      },
    ]

    if (activeId && tree.length > 0) {
      const path = getPathForSelectedTreeNode(tree, activeId)
      if (path) {
        path.forEach((item) => {
          items.push({
            title: (
              <BreadcrumbItem
                item={item}
                onClick={() => handleBreadcrumbNavigate(item)}
                showAllLabel={false}
              />
            ),
            id: getTreeNodeId(item),
          })
        })
      }
    }
    return items
  }, [tree, searchParams, handleBreadcrumbNavigate])

  useEffect(() => {
    if (tree.length === 0) return

    const categoryId = searchParams.get('categoryId') || ''
    const groupId = searchParams.get('groupId') || ''
    const activeId = categoryId || groupId

    if (activeId !== parentId) {
      setParentId(activeId)
    }
  }, [searchParams, tree, setParentId, parentId])

  useEffect(() => {
    if (!debouncedSearchValue.trim() || !tree.length) {
      setRawSearchResults([])
      return
    }

    const searchLower = debouncedSearchValue.toLowerCase().trim()
    const ids = searchTree(tree, searchLower)

    if (ids && ids.length > 0) {
      const cacheKey = `${searchLower}_${ids.join(',')}`

      const cached = searchPathsCache.current[cacheKey]
      if (cached) {
        setRawSearchResults(cached)
        return
      }

      const paths = ids
        .map((id) => getPathForSelectedTreeNode(tree, id))
        .filter((path): path is TreeNode[] => !!path)

      searchPathsCache.current[cacheKey] = paths
      setRawSearchResults(paths)
    } else {
      setRawSearchResults([])
    }
  }, [debouncedSearchValue, tree])

  const items = useMemo((): TreeNode[] => {
    const searchLower = debouncedSearchValue.trim().toLowerCase()
    const rootNodes: readonly TreeNode[] = tree

    if (!searchLower) {
      if (!parentId) return [...rootNodes]
      const stack: TreeNode[][] = [[...rootNodes]]
      while (stack.length > 0) {
        const currentNodes = stack.pop()!
        for (const node of currentNodes) {
          const children = getTreeNodeChildren(node)
          if (getTreeNodeId(node) === parentId) return children
          if (children.length > 0) stack.push(children)
        }
      }
      return []
    }

    const results: TreeNode[] = []
    const visited = new Set<string>()
    const stack: TreeNode[][] = [[...rootNodes]]

    while (stack.length > 0) {
      const nodes = stack.pop()!
      for (const node of nodes) {
        const id = getTreeNodeId(node)
        if (!id || visited.has(id)) continue
        visited.add(id)

        if (getTreeNodeDescription(node).toLowerCase().includes(searchLower)) {
          results.push(node)
        }

        const children = getTreeNodeChildren(node)
        if (children.length > 0) stack.push(children)
      }
    }

    return results
  }, [tree, parentId, debouncedSearchValue])

  const navigateToEditorFeed = useCallback(
    (params: { categoryId?: string; groupId?: string; isLast?: boolean }) => {
      const newParams = new URLSearchParams()

      if (params.categoryId) newParams.set('categoryIds', params.categoryId)
      if (params.groupId) newParams.set('groupId', params.groupId)

      if (currentBrand && Object.keys(currentBrand).length) {
        newParams.set('brandIds', currentBrand.id)
      }

      newParams.set(
        'leftovers',
        params.isLast ? String(withLeftovers) : 'false'
      )
      newParams.set(
        'discontinued',
        params.isLast ? String(withDiscontinued) : 'true'
      )
      newParams.set('page', '0')

      pickCarriedCharacteristics(currentlySearchableCharacteristics).forEach(
        (filter) => {
          const filterWithRange = filter as CharacteristicFilterWithRange

          if (filter.type === 'NUMERIC' && filterWithRange.chosenRange) {
            if (filterWithRange.chosenRange.min != null)
              newParams.append(
                `ranges[${filter.code}].min`,
                String(filterWithRange.chosenRange.min)
              )
            if (filterWithRange.chosenRange.max != null)
              newParams.append(
                `ranges[${filter.code}].max`,
                String(filterWithRange.chosenRange.max)
              )
          } else {
            const values = [
              ...(filter.options || []).map((o) => String(o.value)),
              ...(filter.chosenOptions || []),
            ]
            values.forEach((val) =>
              newParams.append(`filter[${filter.code}]`, val)
            )
          }
        }
      )

      navigate({ pathname: '/nomenclature-feed', search: newParams.toString() })
    },
    [
      currentBrand,
      navigate,
      withLeftovers,
      withDiscontinued,
      currentlySearchableCharacteristics,
    ]
  )

  const handleClickRawSearchResult = (rawSearchResult: TreeNode[]) => {
    if (currentUser?.organisationShortModel?.isLegal === false) {
      toast.warning('Организация не активирована')
      return
    }
    if (!tree || !tree.length) return

    const lastItem = rawSearchResult[rawSearchResult.length - 1]
    const isCategory = isCategoryNode(lastItem)
    const id = getTreeNodeId(lastItem)

    if (id === currentActiveId) {
      setSearchValue('')
      setSearchInputFocused(false)
      return
    }

    const targetNode = getPathForSelectedTreeNode(tree, id)?.pop()
    const targetIsLast = isLastParentNode(targetNode)

    const hasChildren =
      !!targetNode && getTreeNodeChildren(targetNode).length > 0

    if (hasChildren) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set(isCategory ? 'categoryId' : 'groupId', id)
      newParams.delete(isCategory ? 'groupId' : 'categoryId')
      navigate(
        { search: newParams.toString() },
        { state: { scrollToTop: true } }
      )
    } else {
      if (isEditor) {
        if (isCategory) {
          navigateToEditorFeed({ categoryId: id, isLast: targetIsLast })
        } else {
          navigateToEditorFeed({ groupId: id, isLast: targetIsLast })
        }
      } else if (isCategory) {
        search({
          query: '',
          productCodes: [],
          brands: Object.keys(currentBrand).length
            ? [{ ...currentBrand, count: 0 }]
            : [],
          categories: [
            { id, description: getTreeNodeDescription(lastItem), count: 0 },
          ],
          page: 0,
          characteristicFilters: pickCarriedCharacteristics(
            currentlySearchableCharacteristics
          ),
          withLeftovers: withLeftovers,
          stores: currentlySearchableStores,
          isInitial: !useSearchStore.getState().initialFilters,
        })
      }
    }
    setSearchValue('')
    setSearchInputFocused(false)
  }

  const handleClickTile = (
    treeItem: CategoryInterface | NomenclatureGroupInterface
  ) => {
    if (currentUser?.organisationShortModel?.isLegal === false) {
      toast.warning('Организация не активирована', {
        description: 'Пожалуйста, обратитесь к своему менеджеру для активации',
      })
      return
    }

    if (!tree || !tree.length) return

    const isCategory = 'category' in treeItem
    const id = isCategory ? treeItem.category.id : treeItem.nomenclatureGroup.id

    const currentCategoryId = searchParams.get('categoryId')
    const currentGroupId = searchParams.get('groupId')
    if (id === currentCategoryId || id === currentGroupId) return

    const newParams = new URLSearchParams(searchParams)

    const targetIsLast = isLastParentNode(treeItem)

    if (isCategory) {
      const catItem = treeItem as CategoryInterface

      if (catItem.childCategories && catItem.childCategories.length > 0) {
        newParams.set('categoryId', id)
        newParams.delete('groupId')

        navigate(
          { search: newParams.toString() },
          { state: { scrollToTop: true } }
        )
      } else {
        if (isEditor) {
          navigateToEditorFeed({ categoryId: id, isLast: targetIsLast })
        } else {
          search({
            query: '',
            productCodes: [],
            brands: Object.keys(currentBrand).length
              ? [
                  {
                    id: currentBrand.id,
                    description: currentBrand.description,
                    count: 0,
                  },
                ]
              : [],
            categories: [
              {
                id: id,
                description: catItem.category.description,
                count: 0,
              },
            ],
            page: 0,
            characteristicFilters: pickCarriedCharacteristics(
              currentlySearchableCharacteristics
            ),
            withLeftovers: withLeftovers,
            stores: currentlySearchableStores,
            isInitial: !useSearchStore.getState().initialFilters,
          })
        }
      }
    } else if ('nomenclatureGroup' in treeItem) {
      const groupItem = treeItem as NomenclatureGroupInterface

      if (groupItem.childGroups && groupItem.childGroups.length > 0) {
        newParams.set('groupId', id)
        newParams.delete('categoryId')
        navigate(
          { search: newParams.toString() },
          { state: { scrollToTop: true } }
        )
      } else {
        if (isEditor) {
          navigateToEditorFeed({ groupId: id })
        }
      }
    }
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  function getColumnCount() {
    const w = window.innerWidth
    if (w < 480) return 1
    if (w < 768) return 2
    if (w < 1200) return 3
    if (w < 1440) return 4
    if (w < 1850) return 5
    return 6
  }
  const columnCount = getColumnCount()

  const rowCount = Math.ceil(items.length / columnCount)

  const estimateRowHeight = () => {
    const width = window.innerWidth || (parentRef.current?.offsetWidth ?? 1200)
    const tileWidth = (width - GAP * (columnCount - 1)) / columnCount

    const expectedTileHeight = tileWidth * 0.85
    return Math.round(expectedTileHeight + TILE_VERTICAL_PADDING + GAP)
  }

  const estimateSize = useCallback(() => estimateRowHeight(), [columnCount])

  const rowVirtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize,
    overscan: 10,
  })

  useEffect(() => {
    let timeoutId: number

    const handleResize = () => {
      cancelAnimationFrame(timeoutId)
      timeoutId = window.requestAnimationFrame(() => {
        rowVirtualizer.measure()
      })
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      cancelAnimationFrame(timeoutId)
    }
  }, [rowVirtualizer])

  return (
    <>
      {!treeLoading && !loadingError && !!tree.length && (
        <div className={cn.treeTiles}>
          <div className={cn.inner}>
            <div
              className={
                Object.keys(selectedOrganisation).length
                  ? [cn.breadcrumbWithSearch, cn.organisationSelected].join(' ')
                  : cn.breadcrumbWithSearch
              }
            >
              <div className={cn.breadcrumb}>
                <Breadcrumb items={breadcrumbItemsForAntd} separator=">" />
              </div>
              {!hideSearch && (
                <div className={cn.searchWithButton}>
                  <div
                    className={
                      Object.keys(selectedOrganisation).length
                        ? [cn.search, cn.organisationSelected].join(' ')
                        : cn.search
                    }
                  >
                    <input
                      placeholder={`Название ${'category' in tree[0] ? 'категории' : 'nomenclatureGroup' in tree[0] ? 'номенклатурной группы' : ''}`}
                      value={searchValue}
                      onChange={handleSearch}
                      onFocus={() => setSearchInputFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setSearchInputFocused(false), DELAY)
                      }
                      onKeyDown={(e) => {
                        if (e.key == 'Escape') {
                          setSearchInputFocused(false)
                          setTimeout(() => {
                            searchInputRef.current?.blur()
                          }, DELAY)
                        }
                      }}
                      ref={searchInputRef}
                    />
                    {rawSearchResults.length > 0 && searchInputFocused && (
                      <div className={cn.rawSearchList}>
                        {rawSearchResults.map((rawSearchResult) => {
                          const lastItem =
                            rawSearchResult[rawSearchResult.length - 1]
                          const rowKey = `${isCategoryNode(lastItem) ? 'cat' : 'nom'}-${getTreeNodeId(lastItem)}`

                          return (
                            <div
                              className={cn.rawSearchListItem}
                              onClick={() =>
                                handleClickRawSearchResult(rawSearchResult)
                              }
                              key={rowKey}
                            >
                              {rawSearchResult.map((item, idx) => (
                                <span
                                  key={`${isCategoryNode(item) ? 'cat' : 'nom'}-span-${getTreeNodeId(item)}`}
                                >
                                  {getTreeNodeDescription(item)}
                                  {idx < rawSearchResult.length - 1 && ' > '}
                                </span>
                              ))}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                  {actionButton}
                </div>
              )}
            </div>

            <div
              ref={parentRef}
              className={cn.tilesVirtual}
              style={{ '--tile-columns': columnCount }}
            >
              <div
                className={modalOpen ? undefined : cn.tilesVirtualList}
                style={
                  modalOpen
                    ? undefined
                    : {
                        '--virtual-total-size': `${rowVirtualizer.getTotalSize()}px`,
                      }
                }
              >
                {modalOpen ? (
                  <>
                    {Array.from({ length: rowCount }).map((_, rowIndex) => {
                      const start = rowIndex * columnCount
                      const rowItems = items.slice(start, start + columnCount)

                      return (
                        <div key={rowIndex} className={cn.tilesStaticRow}>
                          {rowItems.map((treeItem) => {
                            const key = getTreeNodeId(treeItem)

                            return (
                              <Suspense fallback={null} key={key}>
                                <Tile
                                  tree={tree}
                                  treeItem={treeItem}
                                  setTree={setTree}
                                  handleClickTile={() =>
                                    handleClickTile(treeItem)
                                  }
                                />
                              </Suspense>
                            )
                          })}
                        </div>
                      )
                    })}
                  </>
                ) : (
                  rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const rowIndex = virtualRow.index
                    const start = rowIndex * columnCount
                    const rowItems = items.slice(start, start + columnCount)

                    return (
                      <div
                        key={virtualRow.key}
                        className={cn.tilesVirtualRow}
                        style={{
                          '--virtual-row-size': `${virtualRow.size}px`,
                          '--virtual-row-start': `${virtualRow.start}px`,
                        }}
                      >
                        {rowItems.map((treeItem) => {
                          const key = getTreeNodeId(treeItem)

                          return (
                            <Suspense fallback={null} key={key}>
                              <Tile
                                tree={tree}
                                treeItem={treeItem}
                                setTree={setTree}
                                handleClickTile={() =>
                                  handleClickTile(treeItem)
                                }
                              />
                            </Suspense>
                          )
                        })}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default TreeTiles
